import React, { useContext, useEffect, useRef, useState } from 'react';
import { css, useTheme } from '@emotion/react';
import {
  type RestModalEmailSendModal,
  type RestModalForwards,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { cs } from '@bbkAdminUtils/css-utils';
import { FunnelButton, FunnelTextArea } from '@bbkFunnel/components/v2';
import { type EmotionTheme } from '@bbkFunnel/pages/theme';
import { RefsContext } from '@bbkFunnelUtils/refs.context';
import {
  ModalDesktopInnerSpacer,
  OfferTitleMobile,
  TitleTheme,
  useMediaQueries,
} from '../sections';
import ActionButtonIcons from '../sections/action-button-helpers';

type Props = {
  modalInfo: RestModalEmailSendModal;
  close: () => void;
  handleAction: (
    forwards: RestModalForwards,
    { message }: { message: string }
  ) => void;
};

export const EmailCompose: React.FC<Props> = ({
  modalInfo: {
    id,
    requires: {
      forwards,
      forwards: { icon, text } = {},
      forwards: {
        sync: { message },
      },
      info: { title, placeholder },
    },
  },
  close,
  handleAction,
}) => {
  const { mobileView, tabletView } = useMediaQueries();
  const [messageContent, setMessageContent] = useState(message || '');
  const theme = useTheme() as EmotionTheme;
  const equalButtonSize = theme.complianceEqualButtonSize;
  const refsPrefix = id;
  const refsCtx = useContext(RefsContext);
  const titleRef = useRef<HTMLSpanElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-title`, titleRef), [titleRef]);
  const descRef = useRef<HTMLDivElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-desc`, descRef), [descRef]);
  const btnRef = useRef<HTMLButtonElement>(null);
  useEffect(() => refsCtx?.addRef(`${refsPrefix}-btn`, btnRef), [btnRef]);

  useEffect(() => {
    if (message) setMessageContent(message);
  }, [message]);

  const funnelLabelStyle = {
    display: 'block',
    paddingLeft: '5px',
    paddingRight: '5px',
  };
  if (icon) {
    funnelLabelStyle.display = 'inline-block';
  }

  const buttonDisabled =
    messageContent === '' ||
    messageContent === null ||
    messageContent === undefined;

  return (
    <ModalDesktopInnerSpacer
      css={css`
        ${mobileView && 'padding: 12px 1rem; flex: 1 1 auto;'}
        ${tabletView && 'width: 450px;'}
      `}
      className={cs('email-compose', 'display-grid', 'inner-spacer')}
    >
      <OfferTitleMobile
        title={
          <TitleTheme title={title} css={{ marginBottom: 0 }} ref={titleRef} />
        }
        close={close}
      />
      <div css={{ marginBottom: '1rem' }} ref={descRef}>
        <FunnelTextArea
          name="feedback"
          value={messageContent}
          rows={6}
          placeholder={placeholder ?? 'Start typing...'}
          onChange={(e) => setMessageContent(e.target.value)}
          onBlur={(e) => setMessageContent(e.target.value)}
          maxLength={400}
          modalTextColor
        />
      </div>
      {/* Added padding to make total 27px in mobile for equal size buttons */}
      <div
        className={cs({
          'tw-px-[11px]': equalButtonSize && mobileView,
          'tw-flex tw-justify-center': equalButtonSize && !mobileView,
        })}
      >
        <FunnelButton
          ref={btnRef}
          disabled={buttonDisabled}
          specificStyles={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          onClick={() => handleAction(forwards, { message: messageContent })}
        >
          {icon && <ActionButtonIcons icon={icon} />}
          <span style={funnelLabelStyle}>{text}</span>
        </FunnelButton>
      </div>
    </ModalDesktopInnerSpacer>
  );
};
